import React, { useState, useEffect } from 'react';
import PricePointSelect from "./price_point_select.js";
import currency from 'currency.js';
import styles from './PricePointSelectionForm.module.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';

const PricePointTile = ({ pricePoint, isSelected, onClick, index }) => {
  const handleClick = () => {
    if (pricePoint.capacity > pricePoint.registration_count) {
      onClick(pricePoint.id);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  const tileClass = isSelected ? `${styles.pricePointTile} ${styles.selected}` : styles.pricePointTile;
  const isAvailable = pricePoint.capacity > pricePoint.registration_count;
  const formattedPrice = currency(pricePoint.price_cents / 100, { symbol: "$", precision: 2 }).format();
  
  return (
    <div 
      className={tileClass} 
      onClick={handleClick}
      onKeyPress={handleKeyPress}
      role="radio"
      aria-checked={isSelected}
      aria-disabled={!isAvailable}
      tabIndex={isAvailable ? 0 : -1}
      aria-label={`${pricePoint.title}, ${formattedPrice}${!isAvailable ? ', unavailable' : ''}`}
    >
      <div className={styles.pricePointTitle}>
        {pricePoint.title}
        {isSelected && (
          <FontAwesomeIcon 
            className="active-icon" 
            icon={faCheckCircle} 
            style={{'marginLeft': '15px', top: '2px', position: 'relative' }} 
            aria-hidden="true" 
          />
        )}
      </div>
      <div className={styles.pricePointPrice} aria-hidden="true">
        {formattedPrice}
      </div>
    </div>
  );
};

const PricePointSelectionForm = ({ pricePoints, setPricePointId, nextStep }) => {
  const [selectedPricePointId, setSelectedPricePointId] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!selectedPricePointId) {
      setError('Please select a registration type to continue');
      return;
    }
    setPricePointId(selectedPricePointId);
    nextStep();
  };

  const renderActivePricePointTiles = (pricePoints, selectedPricePointId, handleTileClick) => {
    const availablePricePoints = pricePoints.filter(point => point.capacity > point.registration_count);
    
    return (
      <div 
        className={styles["price-point-tiles"]} 
        role="radiogroup"
        aria-label="Registration Types"
        aria-required="true"
        aria-describedby={error ? "price-point-error" : undefined}
      >
        {availablePricePoints.map((point, index) => (
          <PricePointTile
            key={point.id}
            pricePoint={point}
            isSelected={selectedPricePointId === point.id}
            onClick={handleTileClick}
            index={index}
          />
        ))}
      </div>
    );
  };

  const handleTileClick = (id) => {
    setSelectedPricePointId(id);
    setError('');
  };

  return (
    <form 
      onSubmit={handleSubmit} 
      className={styles.form} 
      aria-labelledby="form-heading"
    >
      <div className="place">
        <h1 id="form-heading" className="form-heading">
          Select Registration Type
        </h1>
      </div>
      
      {error && (
        <div 
          id="price-point-error" 
          className={styles.error} 
          role="alert"
        >
          {error}
        </div>
      )}
      
      {pricePoints.length > 4 ? (
        <PricePointSelect
          price_points={pricePoints}
          onChange={setSelectedPricePointId}
          primary_price_point={{ id: selectedPricePointId }}
          className={styles.field}
        />
      ) : (
        renderActivePricePointTiles(pricePoints, selectedPricePointId, handleTileClick)
      )}
      
      <button 
        type="submit" 
        className={styles.submitButton} 
        style={{
          marginLeft: '7.5px', 
          backgroundColor: '#0dcc8a',
          opacity: selectedPricePointId ? '1' : '0.5',
          cursor: selectedPricePointId ? 'pointer' : 'not-allowed'
        }}
        disabled={!selectedPricePointId}
        aria-disabled={!selectedPricePointId}
      >
        Next
      </button>
    </form>
  );
};

export default PricePointSelectionForm;